import { useRequestDeleteUserImage, useRequestGetVerificationPhotos, useRequestVerifyPhotos } from '@/hooks/httpRequest';
import type { IHooksUseRequestEditVerificationStateParams, IHooksUseRequestVerifyPhotosParams } from '@/interfaces';
import type { VerificationAdditionalCheckStatusEnum, VerificationStateEnum } from '@/enums';
import  { AdminUpdateActionEnum, PhotoGridPositionsEnum, VerifStatusEnum } from '@/lib/enum.ts';
import { PanelInfo, PhotoVerificationGroup } from '@/components/custom';
import { confirm } from '@/components/custom/react-confirm/Confirm.tsx';
import type { IUserPicture, IVerifications } from '@/lib/interface.ts';
import { useInvalidateQueries, useListenEvents } from '@/hooks';
import { notifyInternalMessage } from '@/functions';
import Loader from '@/components/custom/Loader.tsx';
import { useEffect, useState } from 'react';
import { PartyPopper } from 'lucide-react';
import { Button } from '@/components/ui';
import type { GenderEnum } from '@/lib/enum.ts';

interface IState {
	appliedVerifications: Record<string, Omit<IHooksUseRequestVerifyPhotosParams, 'userId'>>;
	verifications: IVerifications[];
}

type TUserData = IVerifications['userData'];
type TUserDataWithExtra = TUserData & {
	extra?: { certificationRequired?: boolean };
};

type TProps = {
	additionalCheckStatus?: VerificationAdditionalCheckStatusEnum;
	additionalCheckState?: VerificationStateEnum;
	filter?: (value: IVerifications) => boolean;
	deniedStateChange?: boolean;
	gender?: GenderEnum;
	readonly?: boolean;
	run?: boolean;
	disableActionInvalidations?: boolean;
};

const CONSTS = {
	initState: {
		appliedVerifications: {},
		verifications: []
	} as IState
};

const VerificationPhotosContainer = ({
	run = true,
	readonly = false,
	filter,
	deniedStateChange,
	disableActionInvalidations = false,
	...props
}: TProps) => {
	const { isLoadingVerifications, verifications } = useRequestGetVerificationPhotos(props, run);
	const { triggerDeleteUserImage } = useRequestDeleteUserImage({ disableInvalidateQueries: true });
	const { triggerVerification } = useRequestVerifyPhotos({ disableInvalidateQueries: true });
	const [state, setState] = useState<IState>(CONSTS.initState);
	const { invalidate } = useInvalidateQueries();

	const withCertificationRequired = (userData: TUserData): TUserData => {
		const casted = userData as TUserDataWithExtra;
		return {
			...casted,
			extra: {
				...(casted.extra ?? {}),
				certificationRequired: true
			}
		} as TUserData;
	};

	const updateUserDataById = (userId: string, updater: (userData: TUserData) => TUserData) => {
		setState(prev => ({
			...prev,
			verifications: prev.verifications.map(item =>
				item.userData?._id !== userId
					? item
					: {
						...item,
						userData: updater(item.userData)
					}
			)
		}));
	};

	const removeItemLocally = (userId: string) => {
		setState(l => ({
			...l,
			verifications: l.verifications.filter(item => item.userData?._id !== userId)
		}));
		window.scroll({ left: 0, top: 0, behavior: 'smooth' });
	};

	const replaceUserPictures = (items: IVerifications[], userId: string, pictures: IUserPicture[]) =>
		items.map(item => (
			item.userId !== userId
				? item
				: {
					...item,
					userData: {
						...item.userData,
						pictures
					}
				}
		));

	useListenEvents({ type: 'userCoinsEdited', callback: () => invalidate() });
	useListenEvents({ type: 'userDeleted', callback: () => invalidate() });
	useListenEvents<{ userId?: string; newGender?: GenderEnum }>({
		type: 'userGenderChangedOptimistic',
		callback: ({ userId, newGender }) => {
			if (!disableActionInvalidations || !userId || !newGender) return;
			updateUserDataById(userId, userData => ({ ...userData, gender: newGender }));
		}
	});
	useListenEvents<{ userId?: string }>({
		type: 'userCertificationRequiredOptimistic',
		callback: ({ userId }) => {
			if (!disableActionInvalidations || !userId) return;
			updateUserDataById(userId, withCertificationRequired);
		}
	});
	useListenEvents({
		type: 'userEdited',
		callback: ({ userId, action }: { userId?: string; action?: AdminUpdateActionEnum }) => {
			if (!disableActionInvalidations) return invalidate();
			if (!userId || !action) return;

			setState(prev => ({
				...prev,
				verifications: prev.verifications.map(item => {
					if (item.userData?._id !== userId) return item;

					const { userData } = item;

					switch (action) {
						case AdminUpdateActionEnum.CHANGE_GENDER:
							return item;
						case AdminUpdateActionEnum.REQUEST_CERTIF:
							return { ...item, userData: withCertificationRequired(userData) };
						default:
							return item;
					}
				})
			}));
		}
	});
	useListenEvents({
		type: 'userWarned',
		callback: () => {
			if (!disableActionInvalidations) {
				invalidate();
			}
		}
	});
	useListenEvents<{ userId?: string }>({
		type: 'userBanned',
		callback: data => {
			if (data?.userId) {
				setState(l => ({
					...l,
					verifications: l.verifications.filter(item => item.userData?._id !== data.userId)
				}));
			}
			invalidate();
		}
	});
	useListenEvents<IHooksUseRequestEditVerificationStateParams>({
		type: 'verificationStateChanged',
		callback: data =>
			setState(l => ({
				...l,
				verifications: [...l.verifications].filter(item => item._id !== data.verificationId)
			}))
	});

	useEffect(() => {
		setState({
			...CONSTS.initState,
			verifications: [...verifications].filter(filter ?? (() => true))
		});
	}, [verifications, filter]);

	const handleOnClickBtnAcceptDeny = (accept: boolean, userId: string, position: PhotoGridPositionsEnum) => {
		const params = { userId } as IHooksUseRequestVerifyPhotosParams;

		if (accept) {
			params.accepted = [position];
		} else {
			params.denied = [position];
		}

		verifyPhotos(params);
	};

	const handleOnClickBtnDeletePicture = async (userId: string, picture: IUserPicture) => {
		const yes = await confirm('Etes-vous sur de vouloir supprimer cette photo du profil de cet utilisateur?');

		if (!yes) {
			return;
		}

		setState(l => ({
			...l,
			verifications: replaceUserPictures(
				l.verifications,
				userId,
				(l.verifications.find(item => item.userId === userId)?.userData.pictures ?? []).filter(pic => pic.path !== picture.path)
			)
		}));

		await triggerDeleteUserImage({ userId, position: picture.position }).then(invalidate).catch(notifyInternalMessage);
	};

	const handleOnClickAcceptDenyAll = (acceptAll: boolean, userId: string) => {
		const verification = state.verifications.find(l => l.userId === userId);

		if (!verification) {
			return;
		}

		const positions = verification.userData.pictures.filter(l => l.status === VerifStatusEnum.PENDING).map(l => l.position);

		const params = { userId } as IHooksUseRequestVerifyPhotosParams;

		if (acceptAll) {
			params.accepted = positions;
		} else {
			params.denied = positions;
		}

		verifyPhotos(params);
	};

	const verifyPhotos = (params: IHooksUseRequestVerifyPhotosParams) => {
		const verification = state.verifications.find(l => l.userId === params.userId);

		if (!verification) {
			return;
		}

		const positions = [] as string[];

		params.accepted && positions.push(...params.accepted);
		params.denied && positions.push(...params.denied);

		const remaningPictures = verification.userData.pictures.filter(l => !positions.includes(l.position));

		// Update state
		let newState = {
			...state,
			// Add verification applied
			appliedVerifications: {
				...state.appliedVerifications,
				[params.userId]: {
					...state.appliedVerifications[params.userId],
					accepted: [...(state.appliedVerifications[params.userId]?.accepted ?? []), ...(params.accepted ?? [])],
					denied: [...(state.appliedVerifications[params.userId]?.denied ?? []), ...(params.denied ?? [])],
					id: verification._id
				}
			},
			// Remove picture verified
			verifications: replaceUserPictures(state.verifications, params.userId, remaningPictures)
		};

		// Update state
		setState(newState);

		// Publish event to complete verification
		if (!remaningPictures.some(l => l.status === VerifStatusEnum.PENDING)) {
			// Update state
			newState = {
				...newState,
				verifications: [...newState.verifications].filter(item => item.userId !== params.userId)
			};
			// Remove verification from remaining verifications
			setState(newState);
			// Scroll to first element of list
			window.scroll({ left: 0, top: 0, behavior: 'smooth' });
			// Sync with server
			triggerVerification({
				...newState.appliedVerifications[params.userId],
				userId: params.userId
			})
				.then(() => {
					Object.keys(newState.appliedVerifications).length !== 0 && newState.verifications.length === 0 && invalidate();
				})
				.catch(notifyInternalMessage);
		}
	};

	if (isLoadingVerifications) {
		return (
			<div className="my-10">
				<Loader className="my-10" />
			</div>
		);
	}

	if (state.verifications.length === 0) {
		return (
			<PanelInfo
				title="Terminé!"
				description={`Vous n'avez actuellement aucune vérification disponible`}
				className="py-10 md:py-28 border-none"
				iconClassName="text-green-500"
				icon={PartyPopper}
			>
				<Button onClick={() => invalidate()} variant={'secondary'} type="button">
					Actualiser la page
				</Button>
			</PanelInfo>
		);
	}

	return (
		<div className={'flex flex-col space-y-4'}>
			{state.verifications.map(item => (
				<PhotoVerificationGroup
					item={item}
					key={item._id}
					readonly={readonly}
					deniedStateChange={deniedStateChange}
					disableActionInvalidations={disableActionInvalidations}
					removeItemLocally={removeItemLocally}
					onClickAcceptDenyBtn={handleOnClickBtnAcceptDeny}
					onClickDeletePictureBtn={handleOnClickBtnDeletePicture}
					onClickAcceptDenyAllBtn={handleOnClickAcceptDenyAll}
				/>
			))}
		</div>
	);
};

export default VerificationPhotosContainer;
